import axiosInstance from '@/core/api'
import type {
	GetDynamicPagePresetsResponse,
	DuplicateDynamicPagePresetResponse,
	GetThemePresetsResponse,
	ApplyThemePresetRequest,
	ApplyThemePresetResponse,
} from '../../type'

import {
	CreateCarouselItemsRequest,
	CreateCarouselItemsResponse,
	UpdateCarouselItemsRequest,
	UpdateCarouselItemsResponse,
	GetCarouselResponse,
	GetLoginAnnouncementResponse,
	CreateLoginAnnouncementRequest,
	CreateLoginAnnouncementResponse,
	UpdateLoginAnnouncementRequest,
	UpdateLoginAnnouncementResponse,
	GetGeneralSettingResponse,
	UpdateGeneralSettingResponse,
	UpdateGeneralSettingRequest,
	GetGameTypeSettingResponse,
	UpdateGameTypeSettingResponse,
	UpdateGameTypeSettingRequest,
	GetDynamicSectionResponse,
	GetDynamicSectionRequest,
	CreateDynamicSectionResponse,
	CreateDynamicSectionRequest,
	UpdateDynamicSectionRequest,
	UpdateDynamicSectionResponse,
	GetDynamicSectionByIdResponse,
	GetDynamicPageResponse,
	CreateDynamicPageResponse,
	CreateDynamicPageRequest,
	UpdateDynamicPageRequest,
	UpdateDynamicPageResponse,
	AddSectionToPageRequest,
	GetDynamicPageByIdResponse,
	UpdateDynamicPageOrderRequest,
	GetDynamicPageRequest,
	GetDynamicPageByIdPayload,
	GetSlideAnnouncementResponse,
	CreateSlideAnnouncementRequest,
	CreateSlideAnnouncementResponse,
	UpdateSlideAnnouncementRequest,
	UpdateSlideAnnouncementResponse,
	GetProfilePageBannersResponse,
	CreateProfilePageBannerRequest,
	CreateProfilePageBannerResponse,
	UpdateProfilePageBannerRequest,
	UpdateProfilePageBannerResponse,
	GetMainWalletSettingResponse,
	UpdateMainWalletSettingRequest,
	UpdateMainWalletSettingResponse,
	GetBrokeNotificationsResponse,
	GetBrokeNotificationByIdResponse,
	CreateBrokeNotificationRequest,
	CreateBrokeNotificationResponse,
	UpdateBrokeNotificationRequest,
	UpdateBrokeNotificationResponse,
} from '../../type'

export const getCarousel = async (): Promise<GetCarouselResponse> => {
	const response =
		await axiosInstance.get<GetCarouselResponse>(`/admin/carousel`)
	return response.data
}

export const createCarouselItems = async (
	payload: CreateCarouselItemsRequest,
): Promise<CreateCarouselItemsResponse> => {
	const response = await axiosInstance.post<CreateCarouselItemsResponse>(
		`/admin/carousel/items`,
		payload,
	)
	return response.data
}

export const updateCarouselItems = async (
	itemId: string,
	payload: UpdateCarouselItemsRequest,
): Promise<UpdateCarouselItemsResponse> => {
	const response = await axiosInstance.patch<UpdateCarouselItemsResponse>(
		`/admin/carousel/items/${itemId}`,
		payload,
	)
	return response.data
}

export const deleteCarouselItem = async (itemId: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/carousel/items/${itemId}`,
	)
	return response.data
}

export const getLoginAnnouncement =
	async (): Promise<GetLoginAnnouncementResponse> => {
		const response = await axiosInstance.get<GetLoginAnnouncementResponse>(
			`/admin/settings/login-announcements`,
		)
		return response.data
	}

export const createLoginAnnouncement = async (
	payload: CreateLoginAnnouncementRequest,
): Promise<CreateLoginAnnouncementResponse> => {
	const response = await axiosInstance.post<CreateLoginAnnouncementResponse>(
		`/admin/settings/login-announcements`,
		payload,
	)
	return response.data
}

export const updateLoginAnnouncement = async (
	id: string,
	payload: UpdateLoginAnnouncementRequest,
): Promise<UpdateLoginAnnouncementResponse> => {
	const response = await axiosInstance.patch<UpdateLoginAnnouncementResponse>(
		`/admin/settings/login-announcements/${id}`,
		payload,
	)
	return response.data
}

export const deleteLoginAnnouncement = async (id: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/settings/login-announcements/${id}`,
	)
	return response.data
}

export const getGeneralSetting =
	async (): Promise<GetGeneralSettingResponse> => {
		const response = await axiosInstance.get<GetGeneralSettingResponse>(
			`/admin/settings/general`,
		)
		return response.data
	}

export const updateGeneralSetting = async (
	payload: UpdateGeneralSettingRequest,
): Promise<UpdateGeneralSettingResponse> => {
	const response = await axiosInstance.patch<UpdateGeneralSettingResponse>(
		`/admin/settings/general`,
		payload,
	)
	return response.data
}

export const getGameTypeSetting =
	async (): Promise<GetGameTypeSettingResponse> => {
		const response = await axiosInstance.get<GetGameTypeSettingResponse>(
			`/admin/settings/game-type`,
		)
		return response.data
	}

export const updateGameTypeSetting = async (
	id: string,
	payload: UpdateGameTypeSettingRequest,
): Promise<UpdateGameTypeSettingResponse> => {
	const response = await axiosInstance.patch<UpdateGameTypeSettingResponse>(
		`/admin/settings/game-type/${id}`,
		payload,
	)
	return response.data
}

export const getDynamicSection = async (
	payload: GetDynamicSectionRequest,
): Promise<GetDynamicSectionResponse> => {
	const response = await axiosInstance.get<GetDynamicSectionResponse>(
		`/admin/settings/dynamic-sections`,
		{
			params: payload,
		},
	)
	return response.data
}

export const createDynamicSection = async (
	payload: CreateDynamicSectionRequest,
): Promise<CreateDynamicSectionResponse> => {
	const response = await axiosInstance.post<CreateDynamicSectionResponse>(
		`/admin/settings/dynamic-sections`,
		payload,
	)
	return response.data
}

export const updateDynamicSection = async (
	id: string,
	payload: UpdateDynamicSectionRequest,
): Promise<UpdateDynamicSectionResponse> => {
	const response = await axiosInstance.patch<UpdateDynamicSectionResponse>(
		`/admin/settings/dynamic-sections/${id}`,
		payload,
	)
	return response.data
}

export const getDynamicSectionById = async (
	id: string,
): Promise<GetDynamicSectionByIdResponse> => {
	const response = await axiosInstance.get<GetDynamicSectionByIdResponse>(
		`/admin/settings/dynamic-sections/${id}`,
	)
	return response.data
}

export const deleteDynamicSection = async (id: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/settings/dynamic-sections/${id}`,
	)
	return response.data
}

export const getDynamicPage = async (
	payload: GetDynamicPageRequest,
): Promise<GetDynamicPageResponse> => {
	const response = await axiosInstance.get<GetDynamicPageResponse>(
		`/admin/settings/dynamic-pages`,
		{
			params: payload,
		},
	)
	return response.data
}

export const createDynamicPage = async (
	payload: CreateDynamicPageRequest,
): Promise<CreateDynamicPageResponse> => {
	const response = await axiosInstance.post<CreateDynamicPageResponse>(
		`/admin/settings/dynamic-pages`,
		payload,
	)
	return response.data
}

export const updateDynamicPage = async (
	id: string,
	payload: UpdateDynamicPageRequest,
): Promise<UpdateDynamicPageResponse> => {
	const response = await axiosInstance.patch<UpdateDynamicPageResponse>(
		`/admin/settings/dynamic-pages/${id}`,
		payload,
	)
	return response.data
}

export const deleteDynamicPage = async (id: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/settings/dynamic-pages/${id}`,
	)
	return response.data
}

export const addSectionToPage = async (
	payload: AddSectionToPageRequest,
): Promise<void> => {
	const response = await axiosInstance.post<void>(
		`/admin/settings/dynamic-pages/${payload.pageId}/sections/${payload.sectionId}`,
	)
	return response.data
}

export const getDynamicPageById = async (
	id: string,
	payload: GetDynamicPageByIdPayload,
): Promise<GetDynamicPageByIdResponse> => {
	const response = await axiosInstance.get<GetDynamicPageByIdResponse>(
		`/admin/settings/dynamic-pages/${id}`,
		{
			params: payload,
		},
	)
	return response.data
}

export const updateDynamicPageOrder = async (
	dynamicPageSectionId: string,
	payload: UpdateDynamicPageOrderRequest,
): Promise<void> => {
	const response = await axiosInstance.patch<void>(
		`/admin/settings/dynamic-sections/sections/${dynamicPageSectionId}/order`,
		payload,
	)
	return response.data
}

export const removeSectionFromPage = async (
	dynamicPageSectionId: string,
): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/settings/dynamic-pages/sections/${dynamicPageSectionId}`,
	)
	return response.data
}

// Slide Announcement Services
export const getSlideAnnouncement = async () => {
	const response = await axiosInstance.get<GetSlideAnnouncementResponse>(
		'/admin/slide-announcements',
	)
	return response.data
}

export const createSlideAnnouncement = async (
	request: CreateSlideAnnouncementRequest,
) => {
	const response = await axiosInstance.post<CreateSlideAnnouncementResponse>(
		'/admin/slide-announcements',
		request,
	)
	return response.data
}

export const updateSlideAnnouncement = async (
	request: UpdateSlideAnnouncementRequest,
) => {
	const response = await axiosInstance.patch<UpdateSlideAnnouncementResponse>(
		'/admin/slide-announcements',
		request,
	)
	return response.data
}

// Profile Page Banner Services
export const getProfilePageBanners =
	async (): Promise<GetProfilePageBannersResponse> => {
		const response = await axiosInstance.get<GetProfilePageBannersResponse>(
			'/admin/profile-page-banners',
		)
		return response.data
	}

export const createProfilePageBanner = async (
	payload: CreateProfilePageBannerRequest,
): Promise<CreateProfilePageBannerResponse> => {
	const response = await axiosInstance.post<CreateProfilePageBannerResponse>(
		'/admin/profile-page-banners',
		payload,
	)
	return response.data
}

export const updateProfilePageBanner = async (
	id: string,
	payload: UpdateProfilePageBannerRequest,
): Promise<UpdateProfilePageBannerResponse> => {
	const response = await axiosInstance.patch<UpdateProfilePageBannerResponse>(
		`/admin/profile-page-banners/${id}`,
		payload,
	)
	return response.data
}

export const deleteProfilePageBanner = async (id: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/profile-page-banners/${id}`,
	)
	return response.data
}

export const getAnnouncementBannerUploadFileUrl = async (request: {
	fileExtension: string
}): Promise<{ presignedUrl: string; fileUrl: string }> => {
	const response = await axiosInstance.post<{
		presignedUrl: string
		fileUrl: string
	}>(`/admin/file/get-announcement-banner-upload-file-url`, request)
	return response.data
}

// Broke Notification Services
export const getBrokeNotifications =
	async (): Promise<GetBrokeNotificationsResponse> => {
		const response = await axiosInstance.get<GetBrokeNotificationsResponse>(
			'/admin/broke-notifications',
		)
		return response.data
	}

export const getBrokeNotificationById = async (
	id: string,
): Promise<GetBrokeNotificationByIdResponse> => {
	const response = await axiosInstance.get<GetBrokeNotificationByIdResponse>(
		`/admin/broke-notifications/${id}`,
	)
	return response.data
}

export const createBrokeNotification = async (
	payload: CreateBrokeNotificationRequest,
): Promise<CreateBrokeNotificationResponse> => {
	const response = await axiosInstance.post<CreateBrokeNotificationResponse>(
		'/admin/broke-notifications',
		payload,
	)
	return response.data
}

export const updateBrokeNotification = async (
	id: string,
	payload: UpdateBrokeNotificationRequest,
): Promise<UpdateBrokeNotificationResponse> => {
	const response = await axiosInstance.patch<UpdateBrokeNotificationResponse>(
		`/admin/broke-notifications/${id}`,
		payload,
	)
	return response.data
}

export const deleteBrokeNotification = async (id: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/broke-notifications/${id}`,
	)
	return response.data
}

// Main Wallet Setting
export const getMainWalletSetting =
	async (): Promise<GetMainWalletSettingResponse> => {
		const response = await axiosInstance.get<GetMainWalletSettingResponse>(
			`/admin/settings/main-wallet`,
		)
		return response.data
	}

export const updateMainWalletSetting = async (
	payload: UpdateMainWalletSettingRequest,
): Promise<UpdateMainWalletSettingResponse> => {
	const response = await axiosInstance.patch<UpdateMainWalletSettingResponse>(
		`/admin/settings/main-wallet`,
		payload,
	)
	return response.data
}

// Dynamic Page Presets
export const getDynamicPagePresets =
	async (): Promise<GetDynamicPagePresetsResponse> => {
		const response = await axiosInstance.get<GetDynamicPagePresetsResponse>(
			`/admin/settings/dynamic-page-presets`,
		)
		return response.data
	}

export const duplicateDynamicPagePreset = async (
	id: string,
): Promise<DuplicateDynamicPagePresetResponse> => {
	const response = await axiosInstance.post<DuplicateDynamicPagePresetResponse>(
		`/admin/settings/dynamic-page-presets/${id}/duplicate`,
	)
	return response.data
}

// Theme Presets
export const getThemePresets = async (): Promise<GetThemePresetsResponse> => {
	const response = await axiosInstance.get<GetThemePresetsResponse>(
		`/admin/settings/theme-presets`,
	)
	return response.data
}

export const applyThemePreset = async (
	payload: ApplyThemePresetRequest,
): Promise<ApplyThemePresetResponse> => {
	const response = await axiosInstance.post<ApplyThemePresetResponse>(
		`/admin/settings/apply-theme-preset`,
		payload,
	)
	return response.data
}
